import { useContext, useState } from 'react';
import SectionContentGroupContext from './SectionContentGroupContext';
import ContentGroupType from '@/lib/constants/challengeModel/ContentGroupType.enum';
import FieldType from '@/lib/constants/challengeModel/FieldType.enum';
import { Button } from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import Space from '@/components/Space';
import Prompt from '@/components/Prompt';
import Dialog from '@/components/Dialog';
import { FormattedMessage } from 'react-intl';
import SectionContext from './SectionContext';
import ModelsContext from '../../ModelsContext';
import useApi from '@/lib/api/useApi';
import { type EditorFieldWithoutRelations } from '@/lib/types';
import SelectedModelIdContext from '../../SelectedModelIdContext';
import EditorIsReadonlyContext from '../../EditorIsReadonlyContext';

type AddModelFieldProps = {
  highestFieldPosition: number;
};

const AddModelField = ({ highestFieldPosition }: AddModelFieldProps) => {
  const api = useApi();

  const [, setModels] = useContext(ModelsContext);
  const selectedModelId = useContext(SelectedModelIdContext);
  const { section } = useContext(SectionContext);
  const sectionContentGroup = useContext(SectionContentGroupContext);

  const [addPromptOpen, setAddPromptOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const saveNewField = async (name: string): Promise<void> => {
    setAddPromptOpen(false);
    setSaving(true);

    const defaultFieldType =
      sectionContentGroup === ContentGroupType.RIGHT
        ? FieldType.DATE
        : FieldType.TEXT;

    const fieldData = {
      name,
      type: defaultFieldType,
      position: highestFieldPosition + 1,
      info: '',
      additionalConfig: JSON.stringify({}),
      contentGroup: sectionContentGroup,
    };

    const field = await api.post<EditorFieldWithoutRelations>(
      `editor/challenge-model/fields?sectionId=${section.id}`,
      fieldData,
    );

    setModels(models => {
      const model = models[selectedModelId];

      model.fields[field.id] = {
        ...field,
        groups: [],
        parts: [],
      };

      model.sections[section.id].fields.push({
        id: field.id,
      });
    });

    setSaving(false);
  };

  const editorIsReadonly = useContext(EditorIsReadonlyContext);

  return (
    <>
      <Button
        variant="outlined"
        color="secondary"
        onClick={() => setAddPromptOpen(true)}
        startIcon={<AddIcon />}
        disabled={saving || editorIsReadonly}
        fullWidth
      >
        <FormattedMessage id="editor.model_editor.add_field.button" />
      </Button>
      <Space height={18} />
      <Prompt
        open={addPromptOpen}
        onCancel={() => setAddPromptOpen(false)}
        onOk={saveNewField}
        OkButton={Dialog.Actions.CreateButton}
        emptyError="prompt.errors.name_empty"
      >
        <Dialog.Title>
          <FormattedMessage id="editor.model_editor.add_field.title" />
        </Dialog.Title>
        <Prompt.TextField
          label={<FormattedMessage id="editor.model_editor.add_field.label" />}
        />
      </Prompt>
    </>
  );
};

export default AddModelField;
